/**
 * De balk onder het antwoord (Brain grote-ronde/75 keuze 10): Doorzetten, Niet
 * goed en Akkoord en versturen. "Opnieuw" bestaat niet meer; een antwoord laten
 * aanpassen gaat via de copiloot.
 *
 * - Akkoord en versturen: de bestaande verzendroute van het vak (mail via
 *   versturen of per-mail met de haak van sessie Mail, chat en WhatsApp via
 *   per-gesprek). Beloftes en handelingen gebeuren dan pas.
 * - Niet goed: het concept gaat niet weg. JP zegt wat er anders moet; dat gaat
 *   naar de copiloot (die zet een nieuw concept in het vak), of alleen als les
 *   ("leer dit ..."). De zaak blijft in Voor jou.
 * - Doorzetten: naar Wacht (wekker met het concept) of naar een collega (mail),
 *   zonder te versturen.
 *
 * Op de iPhone staat de balk op de vaste voet, met Akkoord en versturen als
 * oranje hoofdknop over de hele breedte.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { leerDitApi, lessenSleutel } from "../../Werkplek/Copiloot/leerDit";
import { useV6 } from "../context";
import { Icoon } from "../Icoon";
import { middenApi, middenSleutels, type M7Doorgezet } from "./middenApi";
import { foutTekst, useMenu } from "./vak";
import { OpVastePlek } from "./voetplek";

export interface BalkProps {
  zaakId: string;
  /** De tekst die nu in het vak staat (gaat mee bij doorzetten). */
  tekst: () => string;
  onAkkoord: () => void;
  /** Akkoord en versturen kan niet (leeg vak, geen ontvanger, bezig). */
  akkoordUit: boolean;
  /** Naar een collega kan alleen bij mail. */
  collegaKan: boolean;
  /** Niet goed heeft alleen zin als er een concept van Sanne of Nova staat. */
  nietGoedKan: boolean;
  onDoorgezet: (uitkomst: M7Doorgezet) => void;
}

function Doorzetten({
  zaakId,
  tekst,
  collegaKan,
  onDoorgezet,
}: Pick<BalkProps, "zaakId" | "tekst" | "collegaKan" | "onDoorgezet">) {
  const houder = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useMenu(houder);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const collegas = useQuery({
    queryKey: middenSleutels.collegas,
    queryFn: () => middenApi.collegas(),
    enabled: open && collegaKan,
    staleTime: 5 * 60_000,
  });

  const zet = async (
    naar: "wacht" | "collega",
    gebruikerId: string | null = null,
  ) => {
    setOpen(false);
    setBezig(true);
    setFout(null);
    try {
      const uit = await middenApi.doorzetten(zaakId, {
        naar,
        gebruiker_id: gebruikerId,
        tekst: tekst() || null,
      });
      onDoorgezet(uit);
    } catch (e) {
      setFout(foutTekst(e, "Doorzetten lukte niet. Probeer het opnieuw."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="m7-doorzet" ref={houder}>
      <button
        type="button"
        className="v6-knop stil"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={bezig}
        onClick={() => setOpen(!open)}
      >
        <Icoon naam="uit" klein />
        Doorzetten
      </button>
      {open && (
        <div className="v6-menu" role="menu" aria-label="Doorzetten">
          <button
            type="button"
            role="menuitem"
            onClick={() => void zet("wacht")}
          >
            Naar Wacht, met het concept
          </button>
          {collegaKan &&
            (collegas.data ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                role="menuitem"
                onClick={() => void zet("collega", c.id)}
              >
                Naar {c.naam}
              </button>
            ))}
          {collegaKan && collegas.isLoading && (
            <span className="kopregel">Collega's laden</span>
          )}
        </div>
      )}
      {fout && (
        <span className="v6-fout" role="alert">
          {fout}
        </span>
      )}
    </div>
  );
}

function NietGoed({
  zaakId,
  onSluit,
}: {
  zaakId: string;
  onSluit: () => void;
}) {
  const { vraagCopiloot, openRechts } = useV6();
  const queryClient = useQueryClient();
  const [wat, setWat] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [klaar, setKlaar] = useState<string | null>(null);

  const naarCopiloot = () => {
    if (!wat.trim()) return;
    middenApi.meetBalk(zaakId, "niet_goed");
    vraagCopiloot(`Niet goed. Pas het concept aan: ${wat.trim()}`);
    onSluit();
  };

  const alleenLes = async () => {
    if (!wat.trim()) return;
    setBezig(true);
    setFout(null);
    try {
      const context = { soort: "zaak", id: zaakId };
      await leerDitApi.maak(`Leer dit: ${wat.trim()}`, context);
      middenApi.meetBalk(zaakId, "alleen_les");
      void queryClient.invalidateQueries({ queryKey: lessenSleutel(context) });
      setKlaar("Les klaargezet in de copiloot; het concept blijft staan.");
      openRechts("copiloot");
    } catch (e) {
      setFout(
        foutTekst(e, "De les vastleggen lukte niet. Probeer het opnieuw."),
      );
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="m7-nietgoed" data-testid="m7-nietgoed">
      <label>
        <span>Wat moet er anders?</span>
        <textarea
          value={wat}
          autoFocus
          placeholder="Bijvoorbeeld: geen code aanbieden, eerst vragen sinds wanneer"
          onChange={(e) => setWat(e.target.value)}
        />
      </label>
      {fout && <span className="v6-fout">{fout}</span>}
      {klaar && <span className="m7-klaar">{klaar}</span>}
      <div className="knoppen">
        <button type="button" className="v6-knop" onClick={onSluit}>
          Annuleer
        </button>
        <button
          type="button"
          className="v6-knop"
          disabled={bezig || !wat.trim()}
          onClick={() => void alleenLes()}
        >
          Alleen les vastleggen
        </button>
        <button
          type="button"
          className="v6-knop zacht"
          disabled={!wat.trim()}
          onClick={naarCopiloot}
        >
          Naar de copiloot
        </button>
      </div>
    </div>
  );
}

export function Balk({
  zaakId,
  tekst,
  onAkkoord,
  akkoordUit,
  collegaKan,
  nietGoedKan,
  onDoorgezet,
}: BalkProps) {
  const [nietGoed, setNietGoed] = useState(false);
  return (
    <OpVastePlek plek="balk">
      <div className="m7-balk" data-testid="m7-balk">
        {nietGoed && (
          <NietGoed zaakId={zaakId} onSluit={() => setNietGoed(false)} />
        )}
        <div className="knoppen">
          <Doorzetten
            zaakId={zaakId}
            tekst={tekst}
            collegaKan={collegaKan}
            onDoorgezet={onDoorgezet}
          />
          {nietGoedKan && (
            <button
              type="button"
              className="v6-knop"
              aria-expanded={nietGoed}
              onClick={() => setNietGoed(!nietGoed)}
            >
              <Icoon naam="x" klein />
              Niet goed
            </button>
          )}
          <button
            type="button"
            className="v6-knop doe"
            disabled={akkoordUit}
            onClick={onAkkoord}
          >
            <Icoon naam="stuur" klein />
            Akkoord en versturen
          </button>
        </div>
      </div>
    </OpVastePlek>
  );
}
